import LBox from "@components/LBox";
import LText from "@components/LText";
import { getImportedMusicTracks } from "../../inboxPresentation";
import { InboxTrackRow } from "../InboxTrackRow";
import { fonts } from "@theme";
import { InboxSectionCta, SectionText, SectionTitle } from "../shared";
import type {
  ImportedMusicPayload,
  InboxSection,
  OnNavigate,
  OnPlayTrack,
} from "../types";

const StatCard = ({ value, label }: { value: number; label: string }) => (
  <LBox
    alignItems="center"
    backgroundColor="softNight"
    borderRadius="m-l"
    flex={1}
    gap="s0_5"
    px="s2"
    py="s4"
  >
    <LText color="white" fontFamily={fonts.bold} fontSize={28} lineHeight={28}>
      {value}
    </LText>
    <LText
      color="subGrey"
      fontFamily={fonts.medium}
      fontSize={10}
      letterSpacing={0.5}
      lineHeight={14}
      textTransform="uppercase"
    >
      {label}
    </LText>
  </LBox>
);

/**
 * "Imported music" — either a single "+N new songs matched" delta stat or a
 * three-up songs/artists/playlists grid, with up to three songs beneath a delta.
 */
export const ImportedMusicSection = ({
  section,
  onNavigate,
  onPlayTrack,
}: {
  section: InboxSection;
  onNavigate: OnNavigate;
  onPlayTrack?: OnPlayTrack;
}) => {
  const payload = section.payload as ImportedMusicPayload;
  const tracks = getImportedMusicTracks(payload);
  const isDelta = typeof payload.newlyMatchedSongsCount === "number";

  return (
    <LBox gap="s3_5">
      <SectionTitle>{section.title}</SectionTitle>

      <LBox gap="s3">
        {isDelta ? (
          <LBox
            alignItems="center"
            backgroundColor="softNight"
            borderRadius="l"
            gap="s0_5"
            px="s4"
            py="m1"
          >
            <LText
              color="white"
              fontFamily={fonts.bold}
              fontSize={36}
              lineHeight={36}
            >
              +{payload.newlyMatchedSongsCount}
            </LText>
            <LText
              color="subGrey"
              fontFamily={fonts.medium}
              fontSize={11}
              letterSpacing={0.5}
              lineHeight={14}
              textTransform="uppercase"
            >
              new songs matched
            </LText>
          </LBox>
        ) : (
          <LBox flexDirection="row" gap="s2">
            <StatCard label="songs" value={payload.matchedSongsCount} />
            <StatCard
              label="artists"
              value={payload.importedArtistCount ?? 0}
            />
            <StatCard
              label="playlists"
              value={payload.importedPlaylistCount ?? 0}
            />
          </LBox>
        )}

        {tracks.length > 0 ? (
          <LBox gap="s2">
            {tracks.map((track) => (
              <InboxTrackRow
                key={track.songId}
                onPress={onPlayTrack ? () => onPlayTrack(track) : undefined}
                sentryEventName="inbox_imported_song_press"
                track={track}
              />
            ))}
          </LBox>
        ) : null}

        <SectionText>{section.text}</SectionText>
      </LBox>

      {section.ctaUrl ? (
        <InboxSectionCta
          label={section.ctaText}
          onPress={() => onNavigate(section.ctaUrl)}
        />
      ) : null}
    </LBox>
  );
};
